import { saveMessages, clearMessages, setResponseFeedback } from '@/infrastructure/idb/indexedDb';
import type { TMessage } from '@/models/CopilotChat';

interface ChatRequest {
  user_email: string;
  requester_input: string;
  requester_id: number | string;
  thread_id?: string;
  agent_id?: string;
  return_mocked?: boolean;
  reference_id?: string;
}

interface CreateThreadRequest { 
  ai_response: string;
  internal_id: string;
  source?: any;
  threadId: string;
  sender: 'user' | 'assistant';
  response_feedback?: boolean | null;
  reference_id?: string;
  created_at?: string;
}


export default class CopilotService {
  private readonly apiUrl: string;
  private readonly headers: Record<string, string>;
  private readonly return_mocked: boolean;
  private readonly page_size: number;
  

  constructor(apiUrl: string, isDev: boolean) {
    this.apiUrl = apiUrl;
    this.headers = {
      'Content-Type': 'application/json',
    };
    this.return_mocked = isDev ? true : false;
    this.page_size = 150;
  }

  private async createThread({ ai_response, internal_id, source, threadId, sender, response_feedback, reference_id, created_at }: CreateThreadRequest) {
    await saveMessages(threadId, [{
      id: internal_id,
      thread_id: threadId,
      content: ai_response,
      sender,
      timestamp: created_at ? new Date(created_at) : new Date(),
      source: sender !== 'user' ? source : undefined,
      response_feedback: response_feedback,
      reference_id: reference_id || undefined,
    }] as TMessage[]);
  }

  async createChatNotStream(thread_id: string, request: ChatRequest) {
    request['return_mocked'] = false;
    const response = await fetch(`${this.apiUrl}chat`, {
      method: 'POST',
      body: JSON.stringify(request),
      headers: this.headers,
    });
    const { data }  = await response.json();
    const { ai_response, internal_id, source, thread_id:_thread_id } = data;
    const validedThreadId = thread_id === _thread_id ? thread_id : _thread_id;
    await this.createThread({ ai_response: request.requester_input, internal_id, threadId: validedThreadId, sender: 'user', reference_id: request.reference_id });
    await this.createThread({ ai_response, internal_id, source, threadId: validedThreadId, response_feedback: null, sender: 'assistant', reference_id: request.reference_id });
    return data;
  }

  async createChat(
    thread_id: string,
    request: ChatRequest,
    onMessage?: (msgContent: any) => void
  ) {
    request['return_mocked'] = false;
    const response = await fetch(`${this.apiUrl}chat-stream`, {
      method: 'POST',
      body: JSON.stringify(request),
      headers: this.headers,
    });

    if (!response.ok) {
      console.error('Erro na resposta:', response.statusText);
      return;
    }
  
    if (!response.body) {
      console.error('Resposta sem body');
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      const partes = buffer.split('\n');
      for (let i = 0; i < partes.length - 1; i++) {
        const linha = partes[i].trim();
        if (!linha) continue;

        let jsonStr = linha;
        if (linha.startsWith('data:')) {
          jsonStr = linha.replace('data:', '').trim();
        }

        // Corrige aspas simples para duplas (não é o ideal, mas resolve para backend mal formatado)
        if (jsonStr.startsWith("{'") || jsonStr.startsWith("{ '")) {
          jsonStr = jsonStr.replace(/'/g, '"');
        }

        try {
          const json = JSON.parse(jsonStr);
          if (json.type === 'chat' && onMessage) {
            onMessage(json.content);
          }
        } catch (err) {
           // Tenta capturar o retorno de finalização do stream
          if (linha.includes('Stream completed')) {
            let jsonStr = linha;
            if (linha.startsWith('data:')) {
              jsonStr = linha.replace('data:', '').trim();
            }
            // Normaliza token Python None -> null (mantém aspas simples)
            jsonStr = jsonStr.replace(/\bNone\b/g, 'null');
            let finalObj: any = null;
            try {
              finalObj = JSON.parse(jsonStr);
              if (finalObj && finalObj.data) {
                const { ai_response, internal_id, source, thread_id: _thread_id } = finalObj.data;
                const validedThreadId = thread_id === _thread_id ? thread_id : _thread_id;
                await this.createThread({ ai_response: request.requester_input, internal_id, threadId: validedThreadId, sender: 'user', reference_id: request.reference_id });
                await this.createThread({ ai_response, internal_id, source, threadId: validedThreadId, response_feedback: null, sender: 'assistant', reference_id: request.reference_id });
                // Notifica a UI com payload final incluindo source (evita depender apenas do retorno da promise)
                if (onMessage) {
                  try {
                    onMessage({ content: ai_response, source, internal_id, thread_id: validedThreadId, done: true });
                  } catch (_) {}
                }
                return finalObj.data;
              } else {
                console.log('json.data está indefinido ou mal formatado:', finalObj);
              }
            } catch (_e) {
              try {
                // Fallback tolerante para payloads com aspas simples
                // eslint-disable-next-line no-new-func
                const json = Function('return (' + jsonStr + ')')();
                if (json && json.data) {
                  const { ai_response, internal_id, source, thread_id: _thread_id } = json.data;
                  const validedThreadId = thread_id === _thread_id ? thread_id : _thread_id;
                  await this.createThread({ ai_response: request.requester_input, internal_id, threadId: validedThreadId, sender: 'user', reference_id: request.reference_id });
                  await this.createThread({ ai_response, internal_id, source, threadId: validedThreadId, response_feedback: null, sender: 'assistant', reference_id: request.reference_id });
                  // Notifica a UI com payload final incluindo source
                  if (onMessage) {
                    try { onMessage({ content: ai_response, source, internal_id, thread_id: validedThreadId, done: true }); } catch (_) {}
                  }
                  return json.data;
                }
              } catch (e2) {
                console.log('Erro ao parsear finalização do stream', e2, linha);
              }
            }
          }
        }
      }
      buffer = partes[partes.length - 1];
    }
  }

  async getChat(thread_id: string) {
    const response = await fetch(`${this.apiUrl}chat?thread_id=${thread_id}&page_size=${this.page_size}`);
    const { data, error } = await response.json();
    if (error && error.message) throw new Error(error.message);
    for (const item of data.chat_history) {
      await this.createThread({ ai_response: item.requester_input, internal_id: item.internal_id, threadId: thread_id, sender: 'user', created_at: item.created_at });
      await this.createThread({ ai_response: item.ai_response, internal_id: item.internal_id, source: item.source, threadId: thread_id, response_feedback: item.response_feedback, sender: 'assistant', created_at: item.created_at });
    }
    return data;
  }

  async getChatByReferenceId(requester_id?: string, reference_id?: string) {
    const response = await fetch(`${this.apiUrl}chat/search?requester_id=${requester_id}&reference_id=${reference_id}&page_size=${this.page_size}`);
    const { data, error } = await response.json();
    let thread_id = null;
    if (error && error.message) throw new Error(error.message);
    for (const item of data.conversations[data.conversations.length - 1].chat_history) {
      await this.createThread({ ai_response: item.requester_input, internal_id: item.internal_id, threadId: item.thread_id, sender: 'user', created_at: item.created_at });
      await this.createThread({ ai_response: item.ai_response, internal_id: item.internal_id, source: item.source, threadId: item.thread_id, response_feedback: item.response_feedback, sender: 'assistant', created_at: item.created_at });
      thread_id = item.thread_id
    }
    return thread_id;
  }

  // New: List chat threads with pagination and filters (per /threads/list)
  async listThreads(params: { user_email: string; agent_id?: string; requester_id?: string | number; reference_id?: string | null; start_date?: string | null; end_date?: string | null; page?: number; page_size?: number; }) {
    const query = new URLSearchParams();
    query.set('user_email', params.user_email);
    if (params.reference_id != null && params.reference_id !== '') query.set('reference_id', String(params.reference_id));
    if (params.start_date) query.set('start_date', params.start_date);
    if (params.end_date) query.set('end_date', params.end_date);
    if (params.page) query.set('page', String(params.page));
    query.set('page_size', String(params.page_size ?? 10));
    query.set('agent_id', String(params.agent_id));
    if (params.requester_id) query.set('requester_id', String(params.requester_id));

    const response = await fetch(`${this.apiUrl}threads/list?${query.toString()}`, {
      method: 'GET',
      headers: this.headers,
    });
    const json = await response.json();
    return json;
  }

  // New: Get a specific thread history (per /threads/{thread_id}/history)
  async getThreadHistory(thread_id: string, user_email: string, options?: { reference_id?: string | null; page?: number; page_size?: number; agent_id?: string; requester_id?: string | number; }) {
    const query = new URLSearchParams();
    query.set('user_email', user_email);
    if (options?.reference_id != null && options.reference_id !== '') query.set('reference_id', String(options.reference_id));
    if (options?.page) query.set('page', String(options.page));
    query.set('agent_id', String(options?.agent_id));
    if (options?.requester_id) query.set('requester_id', String(options?.requester_id));
    query.set('page_size', String(options?.page_size ?? this.page_size));

    const response = await fetch(`${this.apiUrl}threads/${encodeURIComponent(thread_id)}/history?${query.toString()}`, {
      method: 'GET',
      headers: this.headers,
    });
    const { data, error } = await response.json();
    if (error && error.message) throw new Error(error.message);

    // Normalize expected payload: prefer data.chat_history, fallback to data.items
    const history = (data && (data.chat_history || data.items)) || [];

    for (const item of history) {
      // The API pairs requester_input and ai_response per entry
      await this.createThread({ ai_response: item.requester_input, internal_id: item.internal_id, threadId: thread_id, sender: 'user', reference_id: item.reference_id, created_at: item.created_at });
      await this.createThread({ ai_response: item.ai_response, internal_id: item.internal_id, source: item.source, threadId: thread_id, response_feedback: item.response_feedback, sender: 'assistant', reference_id: item.reference_id, created_at: item.created_at });
    }

    return data;
  }

  async feedback(internal_id: string, thread_id: string, response_feedback: boolean) {
    const response = await fetch(`${this.apiUrl}chat/feedback`, {
      method: 'PUT',
      body: JSON.stringify({ internal_id, response_feedback }),
      headers: this.headers,
    });
    const data = await response.json();
    await setResponseFeedback(thread_id, internal_id, response_feedback);
    return data;
  }

  async detailedFeedback(internal_id: string, descritive_feedback: string, feedback_tags: string[]) {
    const response = await fetch(`${this.apiUrl}chat/detailed-feedback`, {
      method: 'PUT',
      body: JSON.stringify({ internal_id, descritive_feedback, feedback_tags }),
      headers: this.headers,
    });
    const data = await response.json();
    return data;
  }

  async _clearMessages(thread_id: string) {
    await clearMessages(thread_id);
  }
}
